/**
 * Image hosts the storefront depends on that are not one of the two backends
 * (those live in apiConfig.js).
 *
 *   REACT_APP_PLACEHOLDER_IMAGE_BASE  Placeholder-picture service used when a
 *                                     product has no image or its image fails
 *                                     to load. Default: picsum.photos, which
 *                                     was spelled out at 31 call sites until
 *                                     2026-10-09.
 *
 * The storage hosts are deliberately not configurable: the catalogue API
 * returns Google Cloud Storage *console* URLs (storage.cloud.google.com — a
 * page that needs a Google sign-in), and the browser needs the public host
 * instead. That pair describes how the backend stores images, not a
 * deployment, so it stays a constant.
 */

import { envValue } from "./runtimeEnv";

/** Stripped once here so `${base}/seed/…` can never come out as `//seed`. */
const stripTrailingSlash = (url) => url.replace(/\/+$/, "");

export const PLACEHOLDER_IMAGE_BASE = stripTrailingSlash(
  envValue(
    "REACT_APP_PLACEHOLDER_IMAGE_BASE",
    process.env.REACT_APP_PLACEHOLDER_IMAGE_BASE
  ) || "https://picsum.photos"
);

/**
 * Deterministic placeholder: the same seed always returns the same picture,
 * so a product keeps its stand-in across re-renders and pages.
 * `placeholderImage("abc", 400, 600)` → "https://picsum.photos/seed/abc/400/600".
 */
export const placeholderImage = (seed, width, height) =>
  `${PLACEHOLDER_IMAGE_BASE}/seed/${seed}/${width}/${height}`;

/**
 * Grey "text on a box" placeholder — the admin product table only. `text` is
 * put in the URL verbatim, so pass it pre-encoded ("No+Image").
 */
export const TEXT_PLACEHOLDER_IMAGE_BASE = "https://via.placeholder.com";
export const textPlaceholderImage = (width, height, text) =>
  `${TEXT_PLACEHOLDER_IMAGE_BASE}/${width}x${height}?text=${text}`;

/** The host the catalogue API returns: a console URL that needs a Google sign-in… */
export const STORAGE_HOST_PRIVATE = "storage.cloud.google.com";
/** …and the public host the browser can actually load. */
export const STORAGE_HOST_PUBLIC = "storage.googleapis.com";

/**
 * Rewrite a storage URL to the public host. One definition of a rule that was
 * copied into 15 files (2026-10-09). Non-strings — undefined, null — come back
 * untouched, so `toPublicStorageUrl(x) || fallback` and
 * `toPublicStorageUrl(x)?.trim()` behave exactly like the
 * `x?.replace(…) || fallback` and `x?.replace(…).trim()` they replaced.
 * Replaces every occurrence, as the regex-form callers already did; a URL
 * carries its host once, so this equals the first-occurrence form the other
 * callers used.
 */
export const toPublicStorageUrl = (url) =>
  typeof url === "string"
    ? url.split(STORAGE_HOST_PRIVATE).join(STORAGE_HOST_PUBLIC)
    : url;
